import { actionplansIcon, toClassicIcon } from "common/defaultIcons";
import {
  LAYOUTS,
  MENU_SECTIONS,
  MODULE_SETTINGS,
  TEACHER_ROLES,
} from "shared/constants";
import type { AppRouteRecordRaw } from "shared/types";

import { ACTIONPLAN_ROUTE_NAMES } from "./routeNames";

// The Actieplannen menu mirrors Taken (#1185): an overview of the requests,
// split in Openstaand/Afgerond, and a review page of the individual plans,
// split in Te beoordelen/Beoordeeld. Both pages render their own header
// (breadcrumb + title + tabs), matching "Mijn besprekingen" — hideTitle and
// hideSubmenu keep MainLayout from rendering a second title and tab bar.
// Each tab is its own route with its own page component (like
// /tasks/review/pending|completed), so a tab switch remounts the page — see
// "Tab routes" in .claude/docs/frontend_architecture.md. The tabs used to be
// a ?status= query param, so the parents map a legacy value onto the matching
// tab route.
const routes: AppRouteRecordRaw[] = [
  {
    name: ACTIONPLAN_ROUTE_NAMES.TEACHER_INDEX,
    path: "/actionplans",
    component: () => import("views/EmptyView.vue"),
    redirect: { name: ACTIONPLAN_ROUTE_NAMES.TEACHER_OVERVIEW },
    meta: {
      title: "Actieplannen",
      roleRequired: TEACHER_ROLES,
      settingRequired: [MODULE_SETTINGS.STUDENT_MEETING],
      isMenuItem: true,
      inDropdown: true,
      hasSubmenu: true,
      menuOrder: 2,
      icon: toClassicIcon(actionplansIcon),
      menuSection: MENU_SECTIONS.ORGANISE,
      layout: LAYOUTS.MAIN_NO_WRAPPER,
    },
    children: [
      {
        name: ACTIONPLAN_ROUTE_NAMES.TEACHER_OVERVIEW,
        path: "overview",
        component: () => import("views/EmptyView.vue"),
        redirect: (to) => ({
          name:
            to.query.status === "completed"
              ? ACTIONPLAN_ROUTE_NAMES.TEACHER_OVERVIEW_COMPLETED
              : ACTIONPLAN_ROUTE_NAMES.TEACHER_OVERVIEW_OPEN,
          query: { ...to.query, status: undefined },
        }),
        meta: {
          title: "Actieplannenoverzicht",
          roleRequired: TEACHER_ROLES,
          isMenuItem: true,
          inDropdown: true,
          menuSection: MENU_SECTIONS.ORGANISE,
          hideTitle: true,
          hideSubmenu: true,
        },
        children: [
          {
            name: ACTIONPLAN_ROUTE_NAMES.TEACHER_OVERVIEW_OPEN,
            path: "open",
            component: () =>
              import("actionplan/pages/TeacherOpenActionplansPage.vue"),
            meta: {
              roleRequired: TEACHER_ROLES,
            },
          },
          {
            name: ACTIONPLAN_ROUTE_NAMES.TEACHER_OVERVIEW_COMPLETED,
            path: "completed",
            component: () =>
              import("actionplan/pages/TeacherCompletedActionplansPage.vue"),
            meta: {
              roleRequired: TEACHER_ROLES,
            },
          },
        ],
      },
      {
        name: ACTIONPLAN_ROUTE_NAMES.TEACHER_REVIEW,
        path: "review",
        component: () => import("views/EmptyView.vue"),
        redirect: (to) => ({
          name:
            to.query.status === "graded"
              ? ACTIONPLAN_ROUTE_NAMES.TEACHER_REVIEW_COMPLETED
              : ACTIONPLAN_ROUTE_NAMES.TEACHER_REVIEW_PENDING,
          query: { ...to.query, status: undefined },
        }),
        meta: {
          title: "Actieplannen beoordelen",
          roleRequired: TEACHER_ROLES,
          isMenuItem: true,
          inDropdown: true,
          menuSection: MENU_SECTIONS.ORGANISE,
          hideTitle: true,
          hideSubmenu: true,
        },
        children: [
          {
            name: ACTIONPLAN_ROUTE_NAMES.TEACHER_REVIEW_PENDING,
            path: "pending",
            component: () =>
              import(
                "actionplan/pages/TeacherPendingActionplanReviewsPage.vue"
              ),
            meta: {
              roleRequired: TEACHER_ROLES,
            },
          },
          {
            name: ACTIONPLAN_ROUTE_NAMES.TEACHER_REVIEW_COMPLETED,
            path: "completed",
            component: () =>
              import("actionplan/pages/TeacherReviewedActionplansPage.vue"),
            meta: {
              roleRequired: TEACHER_ROLES,
            },
          },
        ],
      },
    ],
  },
  {
    name: ACTIONPLAN_ROUTE_NAMES.TEACHER_BATCH,
    path: "/actionplans/batches/:batchId",
    component: () => import("actionplan/pages/TeacherActionplanBatchPage.vue"),
    meta: {
      title: "Actieplanverzoek",
      roleRequired: TEACHER_ROLES,
      settingRequired: [MODULE_SETTINGS.STUDENT_MEETING],
      layout: LAYOUTS.MAIN_NO_WRAPPER,
      // The page renders its own header (back button + title + actions).
      hideTitle: true,
    },
  },
];

export default routes;
